/**
 * Server never sends human text: only these codes. Client translates them via `errors.<CODE>` in messages.
 * Adding a code here requires adding `errors.<CODE>` to every messages/<lang>/errors.json.
 */
export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'CURRENCY_CONFIG_CONFLICT',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'RATE_LIMITED',
  /** the instance is overloaded (no DB connection in time): retry in a moment; a repeated bet is safe (idempotency key) */
  'SERVER_BUSY',
  'INTERNAL',
  'EMAIL_TAKEN',
  'NICKNAME_TAKEN',
  'INVALID_CREDENTIALS',
  /** password change: the current password is wrong (not 401 — the session is fine) */
  'WRONG_PASSWORD',
  /** a solved captcha is needed (sign-up; sign-in after failed attempts): get a challenge and retry */
  'CAPTCHA_REQUIRED',
  /** the captcha is wrong, expired, too easy or already used: solve a new one */
  'CAPTCHA_INVALID',
  /** e-mail confirmation link: unknown, expired or already used */
  'EMAIL_TOKEN_INVALID',
  /** the action needs a confirmed e-mail (invite bonus, prize tournaments) */
  'EMAIL_NOT_VERIFIED',
  'INSUFFICIENT_BALANCE',
  'BET_TOO_SMALL',
  'BET_TOO_LARGE',
  'DUPLICATE_BET',
  'ROUND_ALREADY_STARTED',
  'ROUND_NOT_ACTIVE',
  'CASHOUT_TOO_LATE',
  'ROOM_FULL',
  'DAILY_BONUS_ALREADY_CLAIMED',
  /** the daily quest: rounds are still to play (params: done, target) */
  'QUEST_NOT_COMPLETE',
  /** the daily quest reward is already taken today (params: nextAt) */
  'QUEST_ALREADY_CLAIMED',
  'LIMIT_TOKENS_REACHED',
  'LIMIT_TIME_REACHED',
  'ON_BREAK',
  'BANNED',
  'FEATURE_IN_PROGRESS',
  'GAME_IN_PROGRESS',
  'SEED_ROTATION_BLOCKED',
  /** share a moment: the bet is not yours, not settled, a loss or below ×50 (hundredths: SHARE_WIN_MIN_X100) */
  'SHARE_NOT_ELIGIBLE',
  /** account deletion: the typed confirmation is not the player's own nickname */
  'DELETION_CONFIRM_MISMATCH',
  /** account deletion is already requested (cancel it first) */
  'DELETION_PENDING',
  /** Dou Dizhu: an action out of turn (the seq was current) */
  'NOT_YOUR_TURN',
  /** Dou Dizhu: an illegal move; params.reason = INVALID_PATTERN | DOES_NOT_BEAT | MUST_PLAY | NOT_IN_HAND | BAD_BID | BAD_PHASE */
  'INVALID_MOVE',
  /** Dou Dizhu: no open private table with this code */
  'TABLE_NOT_FOUND',
  /** Dou Dizhu: the private table has no free seat */
  'TABLE_FULL',
  /** chess: this pair already played its games for a stake today (UTC); params.limit (anti-collusion, owner 2026-10-02) */
  'CHESS_PAIR_LIMIT',
  /** chat message refused: params.reason = link | empty | long | not_friends | blocked (CHAT_REJECT_REASONS) */
  'CHAT_REJECTED',
  /** sign-in with Google / Apple / Telegram: this provider is not configured on the server */
  'OAUTH_DISABLED',
  /** the player cancelled the provider's sign-in page */
  'OAUTH_CANCELLED',
  /** the provider's answer could not be verified (code exchange, token signature, issuer, audience, expiry, nonce, hash) */
  'OAUTH_FAILED',
  /** the sign-in attempt is unknown, expired, already used or started in another browser: start again */
  'OAUTH_STATE_INVALID',
  /** the provider's confirmed e-mail belongs to an existing account: sign in the usual way and link the provider in the profile */
  'OAUTH_EMAIL_CONFLICT',
  /** this provider account is already linked to another player */
  'OAUTH_IDENTITY_TAKEN',
  /** another account of this provider is already linked to you: unlink it first */
  'OAUTH_ALREADY_LINKED',
  /** the last way to sign in can not be removed */
  'OAUTH_LAST_METHOD',
  /** admin «grant to active players»: more players match than one batch may credit (params.count, params.max) */
  'GRANT_TOO_MANY',
  /** setting a password / deleting an account without one: confirm it is you — sign in again through a provider */
  'REAUTH_REQUIRED',
  /** the provider account used to confirm is not linked to this player */
  'OAUTH_REAUTH_MISMATCH',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiError {
  error: ErrorCode;
  /** machine-readable details (e.g. { min: 10 }) — used as ICU params when translating */
  params?: Record<string, string | number>;
}
